import React, { useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getVendors, createVendor, updateVendor, deleteVendor } from '../../services/api';
import { useApiQuery, useApiMutation } from '../../hooks/useApiQuery';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import useToast from '../../hooks/useToast';

const WalletVendors = () => {
  const context = useOutletContext();
  const eventId = context?.eventId;
  const event = context?.event;
  const { showSuccess, showError } = useToast();
  
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    customId: ''
  });

  // ✅ React Query: Fetch vendors
  const {
    data: allVendors = [],
    isLoading,
    refetch: refetchVendors,
  } = useApiQuery(
    ['vendors'],
    () => getVendors().then(res => res.data),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      onError: () => {
        showError('Failed to load vendors');
      },
    }
  );

  // ✅ Filter vendors for this event
  const vendors = useMemo(() => {
    return allVendors.filter(v => 
      v.managedEvents?.some(e => e._id === eventId || e === eventId)
    );
  }, [allVendors, eventId]);

  // ✅ React Query: Create vendor mutation
  const createMutation = useApiMutation(
    (data) => createVendor(data).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess(`Vendor created for ${event?.name}`);
        handleCloseModal();
        refetchVendors();
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to create vendor');
      },
    }
  );

  // ✅ React Query: Update vendor mutation
  const updateMutation = useApiMutation(
    ({ id, data }) => updateVendor(id, data).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess('Vendor updated successfully');
        handleCloseModal();
        refetchVendors();
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to update vendor');
      },
    }
  );

  // ✅ React Query: Delete vendor mutation
  const deleteMutation = useApiMutation(
    (id) => deleteVendor(id).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess('Vendor deleted');
        refetchVendors();
      },
      onError: () => {
        showError('Failed to delete vendor');
      },
    }
  );

  // ✅ Modal handlers
  const handleOpenModal = useCallback((vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setFormData({
        name: vendor.name,
        email: vendor.email,
        password: '',
        customId: vendor.customId || ''
      });
    } else {
      setEditingVendor(null);
      setFormData({ 
        name: '', 
        email: '', 
        password: '', 
        customId: ''
      });
    }
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingVendor(null);
    setFormData({ name: '', email: '', password: '', customId: '' });
  }, []);

  // ✅ Submit handler
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    if (editingVendor) {
      updateMutation.mutate({ 
        id: editingVendor._id, 
        data: formData 
      });
    } else {
      createMutation.mutate({
        ...formData,
        eventId: eventId
      });
    }
  }, [editingVendor, formData, eventId, createMutation, updateMutation]);

  // ✅ Delete handler
  const handleDelete = useCallback((id, name) => {
    if (!window.confirm(`Delete vendor "${name}"? This action cannot be undone.`)) return;
    deleteMutation.mutate(id);
  }, [deleteMutation]);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading vendors...</p>
        </div>
      </div>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-[#2b3445]">Vendors</h2>
          <p className="text-sm text-[#708090]">
            {event?.name ? `Vendors for ${event.name}` : 'Vendors for this event'}
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          disabled={isSubmitting}
          className="bg-[#1471b9] text-white px-4 py-2 rounded-[8px] text-sm font-bold shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] hover:opacity-90 transition-all flex items-center gap-2"
        >
          <PlusIcon className="w-4 h-4" />
          Add Vendor
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] overflow-hidden">
        {vendors.length === 0 ? (
          <div className="p-8 text-center text-[#708090]">
            No vendors registered for {event?.name || 'this event'}.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-[#dae1e7]">
            <thead className="bg-[#f6f9fc]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Custom ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Balance</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#708090] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#dae1e7]">
              {vendors.map((v) => (
                <tr key={v._id} className="hover:bg-[#f6f9fc] transition-colors">
                  <td className="px-4 py-3 text-sm text-[#2b3445] font-medium">{v.name}</td>
                  <td className="px-4 py-3 text-sm text-[#708090]">{v.email}</td>
                  <td className="px-4 py-3 text-sm text-[#708090]">{v.customId || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                      v.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {v.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-[#2b3445]">
                    ₹{v.walletBalance?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleOpenModal(v)}
                      disabled={isSubmitting}
                      className="p-1 text-[#1471b9] hover:text-[#0f5a9e] transition-colors mr-2 disabled:opacity-50"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(v._id, v.name)}
                      disabled={deleteMutation.isPending}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                    >
                      {deleteMutation.isPending ? (
                        <span className="inline-block w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></span>
                      ) : (
                        <TrashIcon className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[15px] shadow-[0px_4px_16px_0px_rgba(43,52,69,0.1)] w-[500px] max-w-full">
            <div className="p-5 border-b border-[#dae1e7] flex justify-between items-center">
              <h3 className="text-[19.2px] font-semibold text-[#2b3445]">
                {editingVendor ? 'Edit Vendor' : `Add Vendor for ${event?.name || 'Event'}`}
              </h3>
              <button onClick={handleCloseModal} className="text-[#708090] hover:text-[#2b3445]">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#2b3445] mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2b3445] mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                  required
                  disabled={!!editingVendor}
                />
              </div>
              {!editingVendor && (
                <div>
                  <label className="block text-sm font-semibold text-[#2b3445] mb-1">Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                    required={!editingVendor}
                    minLength="6"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-[#2b3445] mb-1">Custom ID (optional)</label>
                <input
                  type="text"
                  value={formData.customId}
                  onChange={(e) => setFormData({ ...formData, customId: e.target.value })}
                  className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20"
                  placeholder="Auto-generated if left empty"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-[#dae1e7] rounded-[8px] text-sm font-bold text-[#2b3445] hover:bg-[#f6f9fc] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-[#1471b9] text-white rounded-[8px] text-sm font-bold hover:opacity-90 transition-all shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : (editingVendor ? 'Update' : 'Create')} Vendor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletVendors;